import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import {
  ORB_FRAGMENT_SHADER,
  ORB_VERTEX_SHADER,
  STATE_PROFILES,
  type OrbState,
} from "./OrbShader";
import { useAudioReactive } from "./useAudioReactive";

interface OrbMeshProps {
  state: OrbState;
  audioLevel: () => number;
  quality: Exclude<OrbRenderQuality, "low">;
}

export type OrbRenderQuality = "high" | "balanced" | "low";

function OrbMesh({ state, audioLevel, quality }: OrbMeshProps): JSX.Element {
  const meshRef = useRef<THREE.Mesh>(null);
  const glowRef = useRef<THREE.Mesh>(null);
  const glowDetail = quality === "high" ? 4 : 3;
  const coreDetail = quality === "high" ? 6 : 5;

  // Uniforms werden zwischen Renders weitergegeben; Werte werden im
  // useFrame-Tick zum Ziel-Profil gelerpt, damit State-Wechsel sanft sind.
  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uSpeed: { value: STATE_PROFILES.idle.speed },
      uDistort: { value: STATE_PROFILES.idle.distort },
      uIntensity: { value: STATE_PROFILES.idle.intensity },
      uAudio: { value: 0 },
      uColorCore: { value: new THREE.Color(...STATE_PROFILES.idle.colorCore) },
      uColorEdge: { value: new THREE.Color(...STATE_PROFILES.idle.colorEdge) },
    }),
    [],
  );

  const targetCore = useRef(new THREE.Color());
  const targetEdge = useRef(new THREE.Color());

  useFrame((_, delta) => {
    const profile = STATE_PROFILES[state];
    targetCore.current.setRGB(...profile.colorCore);
    targetEdge.current.setRGB(...profile.colorEdge);

    const lerp = Math.min(1, delta * 4);
    uniforms.uTime.value += delta;
    uniforms.uSpeed.value += (profile.speed - uniforms.uSpeed.value) * lerp;
    uniforms.uDistort.value += (profile.distort - uniforms.uDistort.value) * lerp;
    uniforms.uIntensity.value += (profile.intensity - uniforms.uIntensity.value) * lerp;
    uniforms.uAudio.value += (audioLevel() - uniforms.uAudio.value) * 0.3;
    uniforms.uColorCore.value.lerp(targetCore.current, lerp);
    uniforms.uColorEdge.value.lerp(targetEdge.current, lerp);

    if (meshRef.current) {
      const t = uniforms.uTime.value * profile.speed * 0.3;
      meshRef.current.rotation.y = t;
      meshRef.current.rotation.x = Math.sin(t * 0.6) * 0.15;
    }
    if (glowRef.current) {
      const pulse = 1 + Math.sin(uniforms.uTime.value * profile.speed * 0.6) * 0.04;
      glowRef.current.scale.setScalar(pulse * 1.35);
    }
  });

  return (
    <group>
      {/* Außerer Glow-Halo */}
      <mesh ref={glowRef}>
        <icosahedronGeometry args={[1, glowDetail]} />
        <shaderMaterial
          uniforms={uniforms}
          vertexShader={ORB_VERTEX_SHADER}
          fragmentShader={ORB_FRAGMENT_SHADER}
          transparent
          depthWrite={false}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Hauptsphere */}
      <mesh ref={meshRef}>
        <icosahedronGeometry args={[1, coreDetail]} />
        <shaderMaterial
          uniforms={uniforms}
          vertexShader={ORB_VERTEX_SHADER}
          fragmentShader={ORB_FRAGMENT_SHADER}
          transparent
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

function Lights(): JSX.Element {
  return (
    <>
      <ambientLight intensity={0.4} />
      <pointLight position={[3, 3, 3]} intensity={0.7} />
      <pointLight position={[-3, -2, -2]} intensity={0.4} color="#7dd3fc" />
    </>
  );
}

function CameraSetup(): null {
  const { camera } = useThree();
  camera.position.set(0, 0, 3.2);
  return null;
}

interface Orb3DProps {
  state: OrbState;
  size?: number;
  /** Optionaler Mic-Stream fuer defensive Web-Audio-Reaktivitaet. */
  audioStream?: MediaStream | null;
  quality?: OrbRenderQuality;
  showLabel?: boolean;
}

function CanvasGuard(): null {
  const { gl } = useThree();
  useEffect(() => {
    const canvas = gl.domElement;
    const onLost = (e: Event): void => {
      e.preventDefault();
      // Three.js fängt das selbst und re-init beim restore
    };
    canvas.addEventListener("webglcontextlost", onLost, false);
    return () => canvas.removeEventListener("webglcontextlost", onLost);
  }, [gl]);
  return null;
}

export function Orb3D({ state, size = 320, audioStream = null, quality = "high", showLabel = true }: Orb3DProps): JSX.Element {
  const profile = STATE_PROFILES[state];
  const audioLevel = useAudioReactive(Boolean(audioStream), audioStream);

  if (quality === "low") {
    return (
      <div className="flex flex-col items-center justify-center select-none" data-orb-renderer="css-fallback">
        <div className="simo-cosmic-orb-fallback" style={{ width: size, height: size }} aria-hidden="true">
          <span />
        </div>
        {showLabel && (
          <div className="mt-4 text-xs uppercase tracking-[0.3em] text-jarvis-muted">
            {profile.label}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center select-none" data-orb-renderer="webgl">
      <div style={{ width: size, height: size }}>
        <Canvas
          dpr={quality === "high" ? [1, 1.5] : [1, 1.15]}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: "low-power",
            preserveDrawingBuffer: false,
          }}
        >
          <CanvasGuard />
          <CameraSetup />
          <Lights />
          <OrbMesh state={state} audioLevel={audioLevel} quality={quality} />
        </Canvas>
      </div>
      {showLabel && (
        <div className="mt-4 text-xs uppercase tracking-[0.3em] text-jarvis-muted">
          {profile.label}
        </div>
      )}
    </div>
  );
}
